import { useEffect, useState } from "react";
import API from "../api/api";

interface Feedback {
  id: number;
  page: string;
  tab: string;
  message: string;
  timestamp: string;
  user: string;
}

const FeedbackPage = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editMessage, setEditMessage] = useState("");

  // Fetch feedbacks
  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await API.get("feedback/");
      setFeedbacks(res.data);
    } catch (err) {
      console.error("Failed to fetch feedbacks:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  // Delete feedback
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this feedback?"))
      return;
    try {
      await API.delete(`feedback/${id}/`);
      setFeedbacks(feedbacks.filter((f) => f.id !== id));
    } catch (err) {
      console.error("Failed to delete feedback:", err);
    }
  };

  // Start editing
  const startEditing = (id: number, currentMessage: string) => {
    setEditingId(id);
    setEditMessage(currentMessage);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditMessage("");
  };

  // Submit edit
  const handleEditSubmit = async (id: number) => {
    try {
      await API.put(`feedback/${id}/`, { message: editMessage });
      setFeedbacks(
        feedbacks.map((f) => (f.id === id ? { ...f, message: editMessage } : f))
      );
      cancelEditing();
    } catch (err) {
      console.error("Failed to update feedback:", err);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">All Feedback</h2>

      {loading ? (
        <p>Loading feedbacks...</p>
      ) : feedbacks.length === 0 ? (
        <p>No feedback submitted yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {feedbacks.map((f) => (
            <div
              key={f.id}
              className="border rounded-lg p-4 bg-white dark:bg-gray-800 dark:text-white shadow hover:shadow-lg transition"
            >
              <p>
                <span className="font-semibold">Page:</span> {f.page}
              </p>
              <p>
                <span className="font-semibold">Tab:</span> {f.tab}
              </p>
              <p>
                <span className="font-semibold">Created by:</span> {f.user}
              </p>
              {editingId === f.id ? (
                <>
                  <textarea
                    className="w-full mt-2 p-2 border rounded-md text-sm dark:bg-gray-700 dark:text-white"
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    rows={4}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleEditSubmit(f.id)}
                      className="bg-green-600 text-white px-3 dark:text-white py-1 rounded hover:bg-green-700 transition"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="bg-gray-500 text-white px-3 dark:text-white py-1 rounded hover:bg-gray-600 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-md break-words">
                    <span className="font-semibold">Message:</span>
                    <p className="mt-1">{f.message}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(f.timestamp).toLocaleString()}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => startEditing(f.id, f.message)}
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(f.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
